/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-empty */
import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import ProfileImg from "../../../assets/profile.png";

// Hosts that actually serve /.netlify/functions/api-proxy. Optimizers like
// NitroPack rehost the widget JS on the WordPress origin while keeping the
// original tools host inside the rewritten path, so match a known host
// anywhere in the src rather than trusting the URL's origin.
const TOOLS_HOSTS = ["tools.unclekam.com", "tools-unclekam.netlify.app"];

function toolsOriginFromSrc(src) {
  if (!src) return "";
  try {
    const u = new URL(src, location.href);
    if (TOOLS_HOSTS.includes(u.hostname)) return u.origin;
  } catch {}
  for (const host of TOOLS_HOSTS) {
    if (src.includes(`/${host}/`) || src.includes(`//${host}/`)) {
      return `https://${host}`;
    }
  }
  return "";
}

function detectToolsOrigin() {
  const fromCur = toolsOriginFromSrc(document.currentScript && document.currentScript.src);
  if (fromCur) return fromCur;
  const scripts = Array.from(document.getElementsByTagName("script"));
  for (let i = scripts.length - 1; i >= 0; i--) {
    const fromScan = toolsOriginFromSrc(scripts[i].src || "");
    if (fromScan) return fromScan;
  }
  const isQA = new URLSearchParams(location.search).has("qa_mode");
  return isQA ? "https://tools-unclekam.netlify.app" : "https://tools.unclekam.com";
}

const ProCard = ({
  pro_id,
  qa_mode,
  show_avatar = true,
  show_badge = true,
  show_name = true,
  show_business = true,
  title_prefix,
  title_font_size = 24,
  title_color = "#000000",
}) => {
  const [pro, setPro] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const fetchedRef = React.useRef("");

  const params = useMemo(() => new URLSearchParams(window.location.search), []);

  const proId = useMemo(
    () => pro_id || params.get("pro_id") || "",
    [pro_id, params]
  );
  const qa = useMemo(
    () => {
      if (typeof qa_mode === "boolean") return qa_mode;
      if (typeof qa_mode === "string") return qa_mode === "true" || qa_mode === "on";
      return params.has("qa_mode");
    },
    [qa_mode, params]
  );

  const PROXY_URL = useMemo(
    () => window.__UK_PROXY_URL__ || `${detectToolsOrigin()}/.netlify/functions/api-proxy`,
    []
  );

  useEffect(() => {
    if (!proId) {
      setLoading(false);
      setError("");
      setPro(null);
      fetchedRef.current = "";
      return;
    }

    // Skip refetch if we already have data for this pro + qa combo
    const key = `${proId}:${qa}`;
    if (fetchedRef.current === key) return;

    let cancelled = false;

    const fetchPro = async () => {
      try {
        setLoading(true);
        setError("");

        const res = await axios.post(
          PROXY_URL,
          {
            path: `/professionals/public-details/${proId}`,
            method: "GET",
            qa_mode: qa,
            raw: true,
          },
          { headers: { "Content-Type": "application/json" } }
        );

        if (cancelled) return;
        fetchedRef.current = key;
        const payload = res.data?.data || res.data || {};
        setPro(payload);
      } catch (err) {
        if (cancelled) return;
        console.error("ProCard fetch error:", err);
        setError("Failed to load professional details.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchPro();
    return () => { cancelled = true; };
  }, [PROXY_URL, qa, proId]);

  if (loading) {
    return (
      <div className="text-center py-4">
        <div className="spinner-border text-primary mb-2" role="status" />
        <div className="text-muted small">Loading...</div>
      </div>
    );
  }

  const defaultTitle = "Book a Free Strategy Call with a Tax Professional";
  const hasTitlePrefix = title_prefix !== undefined && title_prefix !== false && title_prefix !== "";
  const fontSize = typeof title_font_size === "number"
    ? title_font_size
    : parseInt(title_font_size, 10) || 24;

  if (error || !pro) {
    return (
      <div
        className="fw-bold text-center"
        style={{ fontSize, color: title_color || "#000000", fontFamily: "'Open Sans', sans-serif" }}
      >
        {defaultTitle}
      </div>
    );
  }

  const user = pro?.users || {};
  const userpro = user?.userpro || {};

  const fullName = `${user.firstname || ""} ${user.lastname || ""}`.trim();
  const credential = userpro.type || "";

  const avatarArr = user.avatar;
  const avatarUrl =
    (Array.isArray(avatarArr) && avatarArr[0]?.path) ||
    (avatarArr?.path) ||
    ProfileImg;

  const companyName = userpro.businessName || "";
  const badges = user.badges || [];

  // Find the highest-ordering badge to display
  const topBadge = badges.length
    ? badges.reduce((a, b) => (b.ordering > a.ordering ? b : a), badges[0])
    : null;

  const hideAvatar = show_avatar === false || show_avatar === "false";
  const hideBadge = show_badge === false || show_badge === "false";
  const hideName = show_name === false || show_name === "false";
  const hideBusiness = show_business === false || show_business === "false";

  const titleText = hasTitlePrefix
    ? `${typeof title_prefix === "string" ? title_prefix : "Book a Call With"} ${fullName || "Tax Professional"}`
    : "";

  return (
    <div
      className="d-flex flex-column align-items-center text-center"
      style={{ fontFamily: "'Open Sans', sans-serif", margin: "0 auto" }}
    >
      {/* Avatar */}
      {!hideAvatar && (
        <div
          className="rounded-circle overflow-hidden border border-2 border-light shadow-sm mb-2"
          style={{ width: 120, height: 120 }}
        >
          <img
            src={avatarUrl}
            alt={fullName || "Tax Professional"}
            className="w-100 h-100"
            style={{ objectFit: "cover" }}
          />
        </div>
      )}

      {/* Badge */}
      {!hideBadge && topBadge && (
        <div
          className="d-flex align-items-center justify-content-center mb-2"
          style={{
            minWidth: 48,
            height: 48,
            backgroundColor: "#2ec4b6",
            color: "#fff",
            fontWeight: 700,
            fontSize: 12,
            borderRadius: 24,
            marginTop: !hideAvatar ? -30 : 0,
            position: "relative",
            zIndex: 1,
            border: "3px solid #fff",
            padding: "0 12px",
          }}
        >
          {topBadge.name}
        </div>
      )}

      {/* Name + credential */}
      {!hideName && (
        <h6 className="fw-bold mb-1" style={{ fontSize: 18 }}>
          {fullName || "Tax Professional"}
          {credential ? `, ${credential}` : ""}
        </h6>
      )}

      {/* Company */}
      {!hideBusiness && companyName && (
        <div className="text-muted" style={{ fontSize: 14 }}>
          {companyName}
        </div>
      )}

      {/* Title */}
      {titleText && (
        <div
          className="fw-bold mt-3"
          style={{ fontSize, color: title_color || "#000000" }}
        >
          {titleText}
        </div>
      )}
    </div>
  );
};

export default ProCard;
